import { useContext, useEffect, useMemo, useState } from 'react';

import usePagination from '@/hooks/usePagination';
import { useDatePaginatedTransactions } from '@/hooks/useRewardCards';
import PersonContext from '@/pages/Person/PersonContext';
import keyTransactionsByDate from '@/pages/TransactionList/components/DatePaginatedTransactionsList/util/keyTransactionsByDate';
import TransactionItem from '@/pages/TransactionList/components/TransactionItem/TransactionItem';
import Alert from '@/ui/components/Alert';
import Heading from '@/ui/components/Heading';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';
import Paginator from '@/ui/components/Paginator';
import usFormatDateString from '@/utils/usFormatDateString';

const DatePaginatedTransactionsList = () => {
  const { person } = useContext(PersonContext);
  const [page, setPage] = useState('1');
  const [totalPages, setTotalPages] = useState<number>(0);
  const { data, isError, isPending, isSuccess } = useDatePaginatedTransactions(person.id, { page });

  useEffect(() => {
    if (isSuccess) {
      setPage(data?.page.toString() ?? '1');
      setTotalPages(data?.total_pages ?? 0);
    }
  }, [data?.page, data?.total_pages, isSuccess]);

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(+page, totalPages);

  useEffect(() => {
    setPage(currentPage.toString());
  }, [currentPage]);

  const renderData = useMemo(() => {
    if (isPending) {
      return <LoadingSkeleton spinnerProps={{ label: 'Loading transactions...' }} />;
    }

    if (isError) {
      return <Alert level="error">There was an error loading transactions.</Alert>;
    }

    if (data?.results?.length === 0) {
      return <Alert level="info">No transactions found.</Alert>;
    }

    return (
      <div className="flex flex-col">
        {keyTransactionsByDate(data.results).map((day) => {
          const dayKey = Object.keys(day)[0];
          const dayResults = day[dayKey];
          const dateString = usFormatDateString(dayKey);

          return (
            <div key={dayKey} className="mb-4">
              <Heading value={3} text={dateString} />

              <div className="space-y-4 mb-2">
                {dayResults.map((transaction) => {
                  return (
                    <TransactionItem
                      key={transaction.id}
                      transaction={transaction}
                      personTimeZone={person.time_zone}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [data?.results, isError, isPending, person.time_zone]);

  return (
    <div className="mb-4">
      {renderData}

      <Paginator
        currentPage={currentPage}
        goToPage={goToPage}
        nextPage={nextPage}
        prevPage={prevPage}
        totalPages={totalPages}
      />
    </div>
  );
};

export default DatePaginatedTransactionsList;
